import { DragAndDropCard } from "containers/DragAndDrop/DragAndDropCard";
import update from "immutability-helper";
import { useCallback, useEffect, useRef, useState } from "react";

interface ArrayDragAndDropProps {
    items: unknown[];
    itemIds: readonly string[];
    setNewData: (data: unknown[], itemIds: string[]) => void;
    getChild: (index: number, itemId: string) => React.ReactNode;
    canDrag?: boolean;
}

const reorderList = <T,>(list: readonly T[], dragIndex: number, hoverIndex: number): T[] =>
    update(list, {
        $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, list[dragIndex]],
        ],
    });

export const ArrayDragAndDrop = ({
    items,
    itemIds,
    setNewData,
    getChild,
    canDrag,
}: ArrayDragAndDropProps) => {
    const [cards, setCards] = useState(items);
    const [ids, setIds] = useState(() => [...itemIds]);
    const cardsRef = useRef(cards);
    const idsRef = useRef(ids);
    cardsRef.current = cards;
    idsRef.current = ids;

    useEffect(() => {
        setCards(items);
        setIds([...itemIds]);
    }, [items, itemIds]);

    const onMoveCard = useCallback((dragIndex: number, hoverIndex: number) => {
        setCards((prevCards) => {
            const next = reorderList(prevCards, dragIndex, hoverIndex);
            cardsRef.current = next;
            return next;
        });
        setIds((prevIds) => {
            const next = reorderList(prevIds, dragIndex, hoverIndex);
            idsRef.current = next;
            return next;
        });
    }, []);

    const applyReorder = useCallback(() => {
        setNewData(cardsRef.current, [...idsRef.current]);
    }, [setNewData]);

    return cards.map((item, index) => (
        <DragAndDropCard
            key={ids[index]}
            onMoveCard={onMoveCard}
            index={index}
            id={ids[index]}
            applyAction={applyReorder}
            type="arrayItem"
            canDrag={canDrag}
        >
            {getChild(index, ids[index])}
        </DragAndDropCard>
    ));
};
